import React, { useEffect, useState } from "react";
import Select from "react-select";
import apiClient from "../utils/apiClient";
import { SERVER_BASE } from "../config/server";
import Swal from "sweetalert2";
import DateRangePickerComponent from "./CustomDateRangePicker";
import moment from "moment";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { useTable, useGlobalFilter } from "react-table";
import { Link } from "react-router-dom";

const FuelReport = ({ accessOfDepartments }) => {
    const [itemOptions, setItemOptions] = useState([]);
    const [selectedItem, setSelectedItem] = useState(null);

    const [departments, setDepartments] = useState([]);
    const [selectedDept, setSelectedDept] = useState([]);

    const [dateRange, setDateRange] = useState([null, null]);

    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState(null);

    const code = localStorage.getItem("code");
    const token = localStorage.getItem("token");

    // ---------------------------------------------------------------------
    // Fetch item list
    // ---------------------------------------------------------------------
    const fetchItemOptions = async () => {
        try {
            const response = await apiClient.get(
                `${SERVER_BASE}/getSelectiveGroupItem`,
                {
                    params: {
                        code,
                        groupCode: ["O"],
                    },
                    headers: { Authorization: `Bearer ${token}` },
                }
            );

            setItemOptions(
                response.data.map((item) => ({
                    value: item.printName,
                    label: `${item.printName}${item.additionalNames ? ` - [ ${item.additionalNames} ]` : ""
                        }`,
                    itemId: item.id,
                    uom: item.uom,
                }))
            );
        } catch (error) {
            console.error("Error fetching item list:", error);
        }
    };

    useEffect(() => {
        fetchItemOptions();
    }, []);

    // ---------------------------------------------------------------------
    // Departments from props
    // ---------------------------------------------------------------------
    useEffect(() => {
        if (accessOfDepartments) {
            const deptArray = accessOfDepartments.split(",").map((dept) => ({
                value: dept.trim(),
                label: dept.trim(),
            }));
            setDepartments(deptArray);
        }
    }, [accessOfDepartments]);

    // ---------------------------------------------------------------------
    // Get Report Handler
    // ---------------------------------------------------------------------
    const handleGetReport = async () => {
        if (!selectedItem) {
            Swal.fire({
                toast: true,
                position: "top-end",
                icon: "warning",
                title: "Please select inventory name!",
                showConfirmButton: false,
                timer: 1500,
            });
            return;
        }

        if (!selectedDept || selectedDept.length === 0) {
            Swal.fire({
                toast: true,
                position: "top-end",
                icon: "warning",
                title: "Please select at least one department!",
                showConfirmButton: false,
                timer: 2000,
                timerProgressBar: true,
            });
            return;
        }

        const [startDate, endDate] = dateRange;

        if (!startDate || !endDate) {
            Swal.fire({
                toast: true,
                position: "top-end",
                icon: "warning",
                title: "Please select a valid date range!",
                showConfirmButton: false,
                timer: 1500,
            });
            return;
        }

        const payload = {
            itemId: selectedItem.itemId,
            startDate,
            endDate,
            departments: selectedDept.map((d) => d.value),
        };

        try {
            setLoading(true);

            const res = await apiClient.get(`${SERVER_BASE}/getFuelReport`, {
                params: payload,
                headers: { Authorization: `Bearer ${token}` },
            });

            setReportData(res.data);
        } catch (err) {
            console.error("Error fetching report:", err);
        } finally {
            setLoading(false);
        }
    };
    const handleExportExcel = async () => {
        // ---------------- SAFETY CHECK ----------------
        if (!processedItems || processedItems.length === 0) {
            Swal.fire({
                icon: "warning",
                title: "No data to export",
                toast: true,
                position: "top-end",
                timer: 2000,
                showConfirmButton: false,
            });
            return;
        }

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Fuel Ledger");

        /* ======================================================
           REPORT HEADER
        ====================================================== */
        worksheet.addRow(["Fuel Ledger Report"]).font = { bold: true };

        worksheet.addRow([
            `Item: ${selectedItem?.label || "-"}`,
            `From: ${moment(dateRange[0]).format("DD-MMM-YYYY")}`,
            `To: ${moment(dateRange[1]).format("DD-MMM-YYYY")}`,
        ]);

        worksheet.addRow([]); // empty row

        /* ======================================================
           TABLE HEADER
        ====================================================== */
        const headerRow = worksheet.addRow([
            "S.No",
            "Date",
            "Particulars",
            "Allocation",
            "Voucher",
            "Additions",
            "Deductions",
            "Closing Qty",
            "UOM",
        ]);

        headerRow.font = { bold: true };

        /* ======================================================
           TABLE DATA
        ====================================================== */
        processedItems.forEach((row, index) => {
            const isOpening = row.particulars === "Opening Quantity";
            const isClosing = row.particulars === "Closing Quantity";

            const excelRow = worksheet.addRow([
                index + 1,
                row.affectedDate
                    ? moment(row.affectedDate).format("DD-MMM-YYYY")
                    : "",
                row.particulars || row.remarks || "",
                row.allocation || "",
                row.transactionCode || "",
                row.type === "add" ? Number(row.quantity) : "",
                row.type === "less" ? Number(row.quantity) : "",
                Number(row.runningQuantity).toFixed(2),
                row.uom || "",
            ]);

            if (isOpening || isClosing) {
                excelRow.font = { bold: true };
            }
        });

        /* ======================================================
           SAFE AUTO COLUMN WIDTH (NO CRASH)
        ====================================================== */
        worksheet.columns.forEach((col) => {
            let maxLength = 12;

            col.eachCell({ includeEmpty: true }, (cell) => {
                const value = cell.value ? cell.value.toString() : "";
                maxLength = Math.max(maxLength, value.length + 2);
            });

            col.width = maxLength;
        });

        /* ======================================================
           FREEZE HEADER ROWS
        ====================================================== */
        worksheet.views = [{ state: "frozen", ySplit: 4 }];

        /* ======================================================
           DOWNLOAD FILE
        ====================================================== */
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        saveAs(
            blob,
            `Fuel_Ledger_${selectedItem?.value || "Item"}_${moment().format(
                "DDMMYYYY_HHmm"
            )}.xlsx`
        );
    };



    // ---------------------------------------------------------------------
    // Process items for table (Opening → Data → Closing)
    // ---------------------------------------------------------------------
    const processedItems = React.useMemo(() => {
        if (!reportData || !reportData.inventoryRows || !reportData.openingRow)
            return [];

        const { openingRow, inventoryRows } = reportData;

        let runningQty = parseFloat(openingRow.openingQty || 0);

        const openingEntry = {
            affectedDate: dateRange[0],
            allocation: "",
            particulars: "Opening Quantity",
            transactionCode: "",
            type: "",
            quantity: openingRow.openingQty,
            runningQuantity: runningQty,
            uom: selectedItem?.uom,
        };

        const dataRows = inventoryRows.map((row) => {
            const qty = parseFloat(row.quantity) || 0;
            runningQty += row.type === "add" ? qty : -qty;

            return {
                ...row,
                runningQuantity: runningQty,
                uom: selectedItem?.uom,
            };
        });

        const closingEntry = {
            affectedDate: dateRange[1],
            allocation: "",
            particulars: "Closing Quantity",
            transactionCode: "",
            quantity: "",
            type: "",
            runningQuantity: runningQty,
            uom: selectedItem?.uom,
        };

        return [openingEntry, ...dataRows, closingEntry];
    }, [reportData, dateRange, selectedItem]);

    // ---------------------------------------------------------------------
    // Table Columns
    // ---------------------------------------------------------------------
    const columns = React.useMemo(
        () => [
            { Header: "S.No", Cell: ({ row }) => row.index + 1 },
            {
                Header: "Date",
                accessor: "affectedDate",
                Cell: ({ value }) =>
                    value ? moment(value).format("DD-MMM-YYYY") : "-",
            },
            {
                Header: "Particulars",
                accessor: "particulars",
                Cell: ({ row }) =>
                    row.original.particulars || row.original.remarks || "-",
            },
            {
                Header: "Allocation",
                accessor: "allocation",
                Cell: ({ row }) =>
                    row.original.allocation || row.original.remarks || "-",
            },
            {
                Header: 'Voucher',
                accessor: 'transactionCode',
                Cell: ({ row }) => {
                    const { transactionCode, voucherType, voucherId } = row.original;

                    if (!voucherType || !voucherId) {
                        return transactionCode || '-';
                    }

                    let path = "";

                    switch (voucherType) {
                        case "Receipt Voucher":
                            path = `/PreviewExternalReceiptVoucher?voucherId=${voucherId}&type=external`;
                            break;

                        case "Delivery Challan":
                            path = `/previewDeliveryChallan?challanId=${voucherId}&type=challan`;
                            break;

                        case "Addition":
                            path = `/previewInventory?draftId=${voucherId}&type=inventory`;
                            break;

                        case "Deduction":
                            path = `/previewInventory?draftId=${voucherId}&type=inventory`;
                            break;

                        case "Transaction Entry":
                            path = `/TransactionEntryPreview?draftId=${voucherId}&type=transactionEntry`;
                            break;

                        case "Unusable Production":
                            path = `/UnusableProductionPreview?draftId=${voucherId}&type=unusable`;
                            break;

                        case "Conversion":
                            path = `/ItemConversionPreview?conversionId=${voucherId}&type=conversion`;
                            break;
                        case "Issue Slip":
                            path = `/IssueSlipPreview?draftId=${voucherId}&type=issueRequest`;
                            break;
                        default:
                            return transactionCode || '-';
                    }

                    return (
                        <Link
                            to={path}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ textDecoration: "underline" }}
                        >
                            {transactionCode}
                        </Link>
                    );
                },
            },
            {
                Header: "Additions",
                accessor: "additions",
                Cell: ({ row }) => {
                    const { type, quantity } = row.original;
                    if (type === "add") {
                        const val = Number(quantity);
                        return val % 1 === 0 ? val : val.toFixed(2);
                    }
                    return "";
                },
            },
            {
                Header: "Deductions",
                accessor: "deductions",
                Cell: ({ row }) => {
                    const { type, quantity } = row.original;
                    if (type === "less") {
                        const val = Number(quantity);
                        return val % 1 === 0 ? val : val.toFixed(2);
                    }
                    return "";
                },
            },
            {
                Header: "Closing",
                accessor: "runningQuantity",
                Cell: ({ value }) => Number(value).toFixed(2),
            },
            { Header: "UOM", accessor: "uom" },
        ],
        []
    );

    // ---------------------------------------------------------------------
    // React Table Setup — WITHOUT PAGINATION
    // ---------------------------------------------------------------------
    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, state, setGlobalFilter } =
        useTable(
            {
                columns,
                data: processedItems,
            },
            useGlobalFilter
        );

    // ---------------------------------------------------------------------
    // UI
    // ---------------------------------------------------------------------
    return (
        <div className="formcontainer mt-3">
            <h4 className="mb-3">Fuel Ledger</h4>

            {/* Filters */}
            <div className="row align-items-end mb-4">
                <div className="col-md-3">
                    <label className="form-label">Item</label>
                    <Select
                        options={itemOptions}
                        value={selectedItem}
                        onChange={setSelectedItem}
                        placeholder="Select Item"
                    />
                </div>

                <div className="col-md-4">
                    <label className="form-label">Departments</label>
                    <Select
                        isMulti
                        options={departments}
                        value={selectedDept}
                        onChange={setSelectedDept}
                        placeholder="Select Department(s)"
                    />
                </div>

                <div className="col-md-3">
                    <label className="form-label">Date Range</label>
                    <DateRangePickerComponent value={dateRange} onChange={setDateRange} />
                </div>

                {/* Get Button */}
                <div className="col-md-2 d-flex align-items-center">
                    <button
                        className="btn btn-outline-success"
                        onClick={handleGetReport}
                        disabled={loading}
                    >
                        {loading ? "Loading..." : "Get"}
                    </button>
                    {processedItems.length > 0 && (
                        <button
                            className="btn btn-outline-primary ms-2"
                            onClick={handleExportExcel}
                            disabled={!processedItems.length}
                        >
                            Export
                        </button>
                    )}
                </div>

            </div>

            <div className="mt-4">
                {/* Search */}
                <input
                    className="form-control mb-3"
                    placeholder="Search..."
                    value={state.globalFilter || ""}
                    onChange={(e) => setGlobalFilter(e.target.value)}
                />

                <table className="table table-striped table-bordered" {...getTableProps()}>
                    <thead>
                        {headerGroups.map((hg) => (
                            <tr {...hg.getHeaderGroupProps()}>
                                {hg.headers.map((column) => (
                                    <th {...column.getHeaderProps()}>{column.render("Header")}</th>
                                ))}
                            </tr>
                        ))}
                    </thead>

                    <tbody {...getTableBodyProps()}>
                        {rows.map((row, i) => {
                            prepareRow(row);
                            const isOpening = row.original.particulars === 'Opening Quantity';
                            const isClosing = row.original.particulars === 'Closing Quantity';
                            return (
                                <tr {...row.getRowProps()} style={{ fontWeight: isOpening || isClosing ? 'bold' : 'normal' }}>
                                    {row.cells.map(cell => (
                                        <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                                    ))}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

        </div>
    );
};

export default FuelReport;
